import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Paragraph, Select, Subheading, Title } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { LocalizationScope } from 'packages/kakapo-ui-application/contexts/LocalizationScope';

import FeedbackTemplateDescription from './FeedbackTemplateDescription';
import FeedbackTemplateQuestions from './FeedbackTemplateQuestions';
import FeedbackTemplateTitle from './FeedbackTemplateTitle';

const Container = styled(Box)`
	height: 100%;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

function FeedbackTemplateWithData(props: { template: graphql.FeedbackTemplate }) {
	const { t } = useTranslation();

	const [update, { loading }] = graphql.useUpdateFeedbackTemplateMutation();

	const onChange = async (changes: Partial<graphql.UpdateFeedbackTemplateInput>) => {
		await update({
			variables: {
				input: {
					id: props.template.id,
					roles: props.template.roles,
					title: props.template.title,
					description: props.template.description,
					instruction: props.template.instruction,
					questions: props.template.questions.map(({ question }) => ({
						questionID: question.id,
						instruction: [],
					})),
					...changes,
				},
			},
		});
	};

	return (
		<Container py={4}>
			<Box px={4} mb={3}>
				<FeedbackTemplateTitle
					template={props.template}
					onChange={(title) => onChange({ title })}
				/>
				<Flex mt={4}>
					<FeedbackTemplateDescription
						title={t('settings.feedbackLibrary.template.requestDescription.label')}
						value={props.template.description}
						onChange={(description) => onChange({ description })}
					/>
					<Box width={16} flexGrow={0} flexShrink={0} />
					<FeedbackTemplateDescription
						title={t('settings.feedbackLibrary.template.respondDescription.label')}
						value={props.template.instruction}
						onChange={(instruction) => onChange({ instruction })}
					/>
				</Flex>

				<Flex mt={4} alignItems="center">
					<Box mr={2} flexGrow={1} flexShrink={1}>
						<Subheading mb={2}>{t('settings.feedbackLibrary.template.roles.title')}</Subheading>
						<Paragraph>{t('settings.feedbackLibrary.template.roles.description')}</Paragraph>
					</Box>
					<Box flexGrow={0} flexShrink={0}>
						{loading ? (
							<Box width={200}>
								<Select.Skeleton />
							</Box>
						) : (
							<Select
								search={false}
								selectedValue={_.sortBy(props.template.roles).join(',')}
								options={[
									{
										title: t('settings.feedbackLibrary.template.roles.options.cycle'),
										value: [graphql.FeedbackTemplateRole.Cycles]
											.map((role) => role.toString())
											.join(','),
									},
									{
										title: t('settings.feedbackLibrary.template.roles.options.request'),
										value: [
											graphql.FeedbackTemplateRole.Cycles,
											graphql.FeedbackTemplateRole.Request,
										]
											.map((role) => role.toString())
											.join(','),
									},
									{
										title: t('settings.feedbackLibrary.template.roles.options.spontaneous'),
										value: [
											graphql.FeedbackTemplateRole.Cycles,
											graphql.FeedbackTemplateRole.Spontaneous,
										]
											.map((role) => role.toString())
											.join(','),
									},
									{
										title: t('settings.feedbackLibrary.template.roles.options.both'),
										value: [
											graphql.FeedbackTemplateRole.Cycles,
											graphql.FeedbackTemplateRole.Request,
											graphql.FeedbackTemplateRole.Spontaneous,
										]
											.map((role) => role.toString())
											.join(','),
									},
								]}
								onChange={async (roles) => {
									await update({
										variables: {
											input: {
												id: props.template.id,
												roles: roles!.split(',') as graphql.FeedbackTemplateRole[],
												title: props.template.title,
												description: props.template.description,
												instruction: props.template.instruction,
												questions: props.template.questions.map(({ question }) => ({
													questionID: question.id,
													instruction: [],
												})),
											},
										},
									});
								}}
							/>
						)}
					</Box>
				</Flex>
			</Box>
			<Box px={4}>
				<FeedbackTemplateQuestions
					questions={props.template.questions.map(({ question }) => question)}
					onChange={(questions) =>
						onChange({
							questions: questions.map((question) => ({
								questionID: question.id,
								instruction: [],
							})),
						})
					}
				/>
			</Box>
		</Container>
	);
}

export default function FeedbackTemplate(props: { templateID: string }) {
	const { t } = useTranslation();

	const query = graphql.useFeedbackTemplateQuery({
		variables: { id: props.templateID },
		fetchPolicy: 'network-only',
	});

	return query.data ? (
		<LocalizationScope>
			<FeedbackTemplateWithData
				template={query.data.feedbackTemplate as graphql.FeedbackTemplate}
			/>
		</LocalizationScope>
	) : (
		<Container py={4}>
			<Box px={4} mb={3}>
				<Title.Skeleton />
				<Flex mt={4}>
					<FeedbackTemplateDescription.Skeleton
						title={t('settings.feedbackLibrary.template.requestDescription.label')}
					/>
					<Box width={16} flexGrow={0} flexShrink={0} />
					<FeedbackTemplateDescription.Skeleton
						title={t('settings.feedbackLibrary.template.respondDescription.label')}
					/>
				</Flex>
			</Box>
			<Box px={4}>
				<FeedbackTemplateQuestions.Skeleton />
			</Box>
		</Container>
	);
}
